function sizeGuide() {
  return {
    isOpen: false,
    activeTab: "fit-sizing",
    scrollContainers: new Map(), // Store container references and their button states
    scrollTimeout: null,

    /**
     * Initialize the component
     */
    init() {
      this.$nextTick(() => {
        this.initializeScrollContainers();
        this.updateAllButtonStates();
        this.setupKeyboardNavigation();
      });
    },

    /**
     * Initialize scroll containers and their associated buttons
     */
    initializeScrollContainers() {
      const containerRefs = ["bodyScrollContainer", "garmentScrollContainer"];

      containerRefs.forEach((ref) => {
        const container = this.$refs[ref];
        if (container) {
          const section = container.closest(".grid");
          if (section) {
            const leftBtn = section.querySelector(".scroll-left-btn");
            const rightBtn = section.querySelector(".scroll-right-btn");

            this.scrollContainers.set(ref, {
              container,
              leftBtn,
              rightBtn,
              section
            });

            // Add scroll event listener for real-time button state updates
            container.addEventListener(
              "scroll",
              () => {
                this.debouncedUpdateButtonStates(ref);
              },
              { passive: true }
            );

            // Add resize observer to handle dynamic content changes
            if (window.ResizeObserver) {
              const resizeObserver = new ResizeObserver(() => {
                this.updateScrollButtonStates(ref);
              });
              resizeObserver.observe(container);
            }
          }
        }
      });
    },

    /**
     * Setup keyboard navigation for scroll buttons
     */
    setupKeyboardNavigation() {
      document.addEventListener("keydown", (event) => {
        if (!this.isOpen) return;

        const activeElement = document.activeElement;
        const isScrollButton =
          activeElement?.classList.contains("scroll-left-btn") || activeElement?.classList.contains("scroll-right-btn");

        if (isScrollButton) {
          const containerRef = activeElement.getAttribute("aria-controls");
          if (containerRef) {
            if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
              event.preventDefault();
              this.scrollTable(containerRef, "left");
            } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
              event.preventDefault();
              this.scrollTable(containerRef, "right");
            }
          }
        }
      });
    },

    /**
     * Debounced function to update button states
     */
    debouncedUpdateButtonStates(containerRef) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.updateScrollButtonStates(containerRef);
      }, 100);
    },

    /**
     * Scroll table container in specified direction
     */
    scrollTable(containerRef, direction) {
      const containerData = this.scrollContainers.get(containerRef);
      if (!containerData?.container) {
        console.warn(`Scroll container not found: ${containerRef}`);
        return;
      }

      const { container } = containerData;
      const scrollAmount = Math.min(200, container.clientWidth * 0.8); // Responsive scroll amount
      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;

      // Check if scroll is possible
      if (direction === "left" && currentScroll <= 0) {
        return;
      }

      if (direction === "right" && currentScroll >= maxScroll) {
        return;
      }

      // Calculate target scroll position
      const targetScroll =
        direction === "left"
          ? Math.max(0, currentScroll - scrollAmount)
          : Math.min(maxScroll, currentScroll + scrollAmount);

      // Perform smooth scroll
      container.scrollTo({
        left: targetScroll,
        behavior: "smooth"
      });

      // Update button states after scroll animation
      setTimeout(() => {
        this.updateScrollButtonStates(containerRef);
      }, 300);
    },

    /**
     * Update scroll button states for a specific container
     */
    updateScrollButtonStates(containerRef) {
      const containerData = this.scrollContainers.get(containerRef);
      if (!containerData) {
        return;
      }

      const { container, leftBtn, rightBtn } = containerData;

      if (!container) {
        return;
      }

      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const canScrollLeft = currentScroll > 0;
      const canScrollRight = currentScroll < maxScroll;

      // Update button states
      if (leftBtn) {
        leftBtn.disabled = !canScrollLeft;
        leftBtn.setAttribute("aria-hidden", !canScrollLeft);
        leftBtn.setAttribute("aria-label", canScrollLeft ? "Scroll left" : "Cannot scroll left");
      }

      if (rightBtn) {
        rightBtn.disabled = !canScrollRight;
        rightBtn.setAttribute("aria-hidden", !canScrollRight);
        rightBtn.setAttribute("aria-label", canScrollRight ? "Scroll right" : "Cannot scroll right");
      }
    },

    /**
     * Update all button states
     */
    updateAllButtonStates() {
      this.scrollContainers.forEach((_, containerRef) => {
        this.updateScrollButtonStates(containerRef);
      });
    },

    /**
     * Handle drawer open event
     */
    handleDrawerOpen() {
      this.isOpen = true;
      this.$nextTick(() => {
        // Re-initialize containers in case DOM has changed
        this.initializeScrollContainers();
        this.updateAllButtonStates();
      });
    },

    /**
     * Clean up event listeners when component is destroyed
     */
    destroy() {
      this.scrollContainers.forEach((containerData) => {
        if (containerData.container) {
          containerData.container.removeEventListener("scroll", () => {
            this.debouncedUpdateButtonStates(containerData.container);
          });
        }
      });
      this.scrollContainers.clear();
      clearTimeout(this.scrollTimeout);
    }
  };
}

export default sizeGuide;
